import type { App } from "vue";
import type { Router } from "vue-router";

import { BackendError, CredentialsError, TooManyRequestsError } from "@/core/errors/api_errors";
import { isStaleChunkError } from "@/shared/lib/staleChunkReload";

const TRACE_PROPAGATION_TARGETS: Array<string | RegExp> = [
	"ofc-test-01.tspb.su",
	"m.rulse.ru",
	"rulse.ru",
	"dev.tspb.su",
	/^\//,
];

const extractHttpStatus = (exception: unknown): number | null => {
	if (!exception || typeof exception !== "object") {
		return null;
	}

	const exceptionWithCode = exception as { code?: unknown; status?: unknown; message?: unknown };

	if (typeof exceptionWithCode.code === "number") {
		return exceptionWithCode.code;
	}

	if (typeof exceptionWithCode.status === "number") {
		return exceptionWithCode.status;
	}

	if (typeof exceptionWithCode.message !== "string") {
		return null;
	}

	try {
		const parsed = JSON.parse(exceptionWithCode.message) as { code?: unknown; status?: unknown };
		if (typeof parsed.code === "number") {
			return parsed.code;
		}
		if (typeof parsed.status === "number") {
			return parsed.status;
		}
	} catch {
		return null;
	}

	return null;
};

const isIgnoredApiError = (exception: unknown): boolean =>
	exception instanceof CredentialsError ||
	exception instanceof BackendError ||
	exception instanceof TooManyRequestsError ||
	(exception instanceof Error &&
		(exception.name === "CredentialsError" ||
			exception.name === "BackendError" ||
			exception.name === "TooManyRequestsError"));

const isSentryHost = (hostname: string): boolean => hostname === "ofc-test-01.tspb.su" || hostname.includes("rulse.ru");

const getSentryDsn = (): string | undefined => {
	if (import.meta.env.DEV) {
		return undefined;
	}

	const dsn = import.meta.env.VITE_SENTRY;
	if (typeof dsn !== "string" || dsn === "" || dsn === "dev_key") {
		return undefined;
	}

	if (!isSentryHost(window.location.hostname)) {
		return undefined;
	}

	return dsn;
};

export const initSentry = async (app: App, router: Router): Promise<void> => {
	const dsn = getSentryDsn();
	if (!dsn) {
		return;
	}

	const Sentry = await import("@sentry/vue");

	Sentry.init({
		app,
		dsn,
		release: import.meta.env.SENTRY_RELEASE || undefined,
		logErrors: true,
		integrations: [
			Sentry.browserTracingIntegration({
				router,
			}),
		],
		ignoreErrors: [
			/^CredentialsError$/,
			/^BackendError$/,
			/^TooManyRequestsError$/,
			/Unable to preload CSS for/i,
			/Failed to fetch dynamically imported module/i,
			/Socket.IO unexpected disconnect/i,
		],
		beforeSend(event, hint) {
			const originalException = hint.originalException;
			const statusCode = extractHttpStatus(originalException);

			if (
				statusCode === 401 ||
				statusCode === 403 ||
				isIgnoredApiError(originalException) ||
				isStaleChunkError(originalException)
			) {
				return null;
			}

			return event;
		},
		tracesSampleRate: 0.1,
		tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
	});

	void Sentry.lazyLoadIntegration("replayIntegration")
		.then((replayIntegration) => {
			Sentry.addIntegration(
				replayIntegration({
					maskAllText: false,
					maskAllInputs: false,
					blockAllMedia: false,
				}),
			);
		})
		.catch(() => {
			// Replay необязателен: tracing продолжит работать без него
		});

	app.config.performance = true;
};
